import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, FileText, Truck, ScanBarcode, MoreHorizontal, Thermometer, ClipboardList, Bell, Shield, LogOut, Monitor } from 'lucide-react';
import { useMobileAuth } from '../MobileAuthProvider';
import { useExperienceFor } from '../../experience/experience';

/**
 * Bottom tab bar (A14).
 *
 * At most five items, chosen by role. The role is read the same way
 * MobileNavigation reads it (`useMobileAuth().role`), so the two agree.
 *
 * "More" lists everything the role can reach that is not already a tab,
 * including Notifications with an unread badge, Security and Sign out.
 */

/**
 * The role values MobileAuthProvider assigns (its USER_ROLES map). Duplicated as
 * plain strings on purpose: this module is rendered inside tests that partially
 * mock the provider, and a constant must not disappear because of a mock.
 */
export const ROLE = Object.freeze({
  ADMIN: 'admin',
  DEPARTMENT_HEAD: 'department_head',
  TEAM_MEMBER: 'team_member',
  STAFF: 'staff',
  DRIVER: 'driver',
});

const TABS_BY_ROLE = {
  manager: [
    { key: 'home', label: 'Home', path: '/mobile/dashboard', icon: Home },
    { key: 'invoices', label: 'Invoices', path: '/mobile/invoices', icon: FileText },
    { key: 'receive', label: 'Receive', path: '/mobile/receiving', icon: Truck },
    { key: 'scan', label: 'Scan', path: '/mobile/scan', icon: ScanBarcode },
    { key: 'more', label: 'More', path: null, icon: MoreHorizontal },
  ],
  kitchen: [
    { key: 'home', label: 'Home', path: '/mobile/boh', icon: Home },
    { key: 'temp', label: 'Temp', path: '/mobile/boh/temperature', icon: Thermometer },
    { key: 'stock', label: 'Stock', path: '/mobile/boh/stock-count', icon: ClipboardList },
    { key: 'scan', label: 'Scan', path: '/mobile/scan', icon: ScanBarcode },
    { key: 'more', label: 'More', path: null, icon: MoreHorizontal },
  ],
  driver: [
    { key: 'home', label: 'Home', path: '/mobile/driver', icon: Home },
    { key: 'receive', label: 'Receive', path: '/mobile/receiving', icon: Truck },
    { key: 'scan', label: 'Scan', path: '/mobile/scan', icon: ScanBarcode },
    { key: 'more', label: 'More', path: null, icon: MoreHorizontal },
  ],
};

/** Which tab set a role gets. Managers and heads get the manager set. */
export function tabsForRole(role) {
  if (role === ROLE.ADMIN || role === ROLE.DEPARTMENT_HEAD) return TABS_BY_ROLE.manager;
  if (role === ROLE.DRIVER) return TABS_BY_ROLE.driver;
  if (role === ROLE.STAFF) return TABS_BY_ROLE.kitchen;
  return TABS_BY_ROLE.kitchen;
}

/** Everything "More" offers, minus whatever is already a tab. */
export function moreItemsForRole(role) {
  const tabs = tabsForRole(role);
  const tabPaths = new Set(tabs.map((t) => t.path).filter(Boolean));
  const all = [
    { key: 'notifications', label: 'Notifications', path: '/mobile/notifications', icon: Bell },
    { key: 'invoices', label: 'Invoices', path: '/mobile/invoices', icon: FileText },
    { key: 'receiving', label: 'Receive a delivery', path: '/mobile/receiving', icon: Truck },
    { key: 'scan', label: 'Scan', path: '/mobile/scan', icon: ScanBarcode },
    { key: 'inventory', label: 'Inventory', path: '/mobile/inventory', icon: ClipboardList },
    { key: 'safety', label: 'Safety', path: '/mobile/safety', icon: Shield },
    { key: 'security', label: 'Security', path: '/mobile/security', icon: Shield },
  ];
  return all.filter((item) => !tabPaths.has(item.path));
}

export default function MobileTabBar({ unreadCount = 0, onSignOut = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, token, logout } = useMobileAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  // The role picks the tab set; the organisation's modules (server experience) decide which of them exist here.
  const exp = useExperienceFor(token);
  const tabOn = (key) => exp.mobileTabs.has(key);

  const tabs = tabsForRole(role).filter((t) => tabOn(t.key));

  const isActive = (path) => path && (location.pathname === path || location.pathname.startsWith(`${path}/`));

  const go = useCallback((path) => {
    setMoreOpen(false);
    navigate(path);
  }, [navigate]);

  const signOut = useCallback(async () => {
    setMoreOpen(false);
    if (onSignOut) { onSignOut(); return; }
    if (typeof logout === 'function') { await logout(); }
    navigate('/mobile/login');
  }, [logout, navigate, onSignOut]);

  // Close "More" when the route changes underneath it.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  return (
    <>
      {moreOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          data-testid="mobile-more-sheet"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute inset-x-0 bottom-0 bg-surface rounded-t-2xl p-4"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-base text-ink mb-3">More</h2>
            <ul className="space-y-1">
              {moreItemsForRole(role).filter((item) => tabOn(item.key)).map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => go(item.path)}
                      data-testid={`mobile-more-${item.key}`}
                      className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] rounded text-left text-ink"
                    >
                      <Icon className="w-5 h-5 text-ink-muted" />
                      <span>{item.label}</span>
                      {item.key === 'notifications' && unreadCount > 0 && (
                        <span
                          className="ml-auto min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 text-white"
                          data-testid="mobile-more-unread-badge"
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              <li>
                <a
                  href="/cost-intel"
                  data-testid="mobile-more-full-app"
                  className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] rounded text-left text-ink"
                >
                  <Monitor className="w-5 h-5 text-ink-muted" />
                  <span>Open full app</span>
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={signOut}
                  data-testid="mobile-more-signout"
                  className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] rounded text-left text-danger"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign out</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-30 bg-surface border-t border-surface-border"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="mobile-tab-bar"
        aria-label="Primary"
      >
        <div className="flex justify-around">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.path ? isActive(tab.path) : moreOpen;
            return (
              <button
                key={tab.key}
                type="button"
                data-testid={`mobile-tab-${tab.key}`}
                aria-current={active ? 'page' : undefined}
                onClick={() => (tab.path ? go(tab.path) : setMoreOpen((v) => !v))}
                className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[44px] ${
                  active ? 'text-primary-500' : 'text-ink-muted'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs mt-0.5">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
